import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';

/**
 * Facebook in-app browser injects autofill bridge scripts that can throw with no app frames.
 * See THEOBP-SITE-8 — stack includes setContactAutofillValuesFromBridge.
 */
const UNDEFINED_VALUE_READ_PATTERN =
  /Cannot read properties of undefined \(reading ['"]value['"]\)/;

function stackOrFramesMentionBridge(event: ErrorEvent, hint: EventHint): boolean {
  const err = hint.originalException;
  if (err instanceof Error && err.stack?.includes('setContactAutofillValuesFromBridge')) {
    return true;
  }
  const frames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
  for (const f of frames) {
    const fn = f.function ?? '';
    if (fn.includes('setContactAutofillValuesFromBridge')) {
      return true;
    }
  }
  const primary = event.exception?.values?.[0];
  if (primary?.value?.includes('setContactAutofillValuesFromBridge')) {
    return true;
  }
  return false;
}

/** Exported for unit tests */
export function facebookWebViewAutofillNoiseFilter(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const err = hint.originalException;
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  const fallbackMsg = event.exception?.values?.[0]?.value ?? '';
  const combined = msg || fallbackMsg;

  if (!UNDEFINED_VALUE_READ_PATTERN.test(combined)) {
    return event;
  }
  if (!stackOrFramesMentionBridge(event, hint)) {
    return event;
  }
  return null;
}

export function initSentry(): void {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!dsn) {
    console.warn('[sentry] Sentry DSN not set — error monitoring disabled');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend: facebookWebViewAutofillNoiseFilter,
  });
}
